import React, { useState, useEffect } from 'react';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

interface Subscription {
  _id: string;
  name: string;
  price: string;
  duration: string;
  users: string;
  leadAccess: string;
  dailyUnlock: string;
  transactionId: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  createdAt: string;
  startDate?: string;
  expiryDate?: string;
}

export default function BrokerPurchasedPlans() {
  const { token } = useAuth();
  const { theme } = useTheme();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptions = async () => {
    try {
      if (!token) return;
      setLoading(true);
      const data = await api.getSubscriptions(token);
      const subs = Array.isArray(data) ? data : [];
      setSubscriptions(subs);
    } catch (err) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchSubscriptions();
  }, [token]);

  const calculateRemainingDays = (expiryDate?: string) => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diff = expiry.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };



  return (
        <UserDashboardLayout>
            <div className="mb-10">
                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 p-2 md:p-0">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2 leading-none">Purchased Plans</h1>
                        <p className="text-slate-500 font-medium text-sm md:text-base">View and manage your active and past subscriptions.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={fetchSubscriptions}
                            className="inline-flex items-center gap-3 px-6 py-3.5 bg-white border border-gray-200 text-slate-700 hover:bg-slate-50 text-[12px] md:text-[14px] font-bold rounded-md transition-all shadow-sm active:scale-95"
                        >
                            <i className="fa-solid fa-arrows-rotate"></i>
                            Refresh
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm mx-2 md:mx-0">
                        <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="font-bold text-[#05144b] text-[14px]">Loading subscriptions...</p>
                    </div>
                ) : subscriptions.length === 0 ? (
                    <div className="bg-white dark:bg-slate-900 rounded-md p-16 text-center border border-gray-100 dark:border-slate-800 shadow-sm mx-2 md:mx-0">
                        <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className="fa-solid fa-file-invoice-dollar text-3xl text-slate-300 dark:text-slate-600"></i>
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Subscriptions Found</h2>
                        <p className="text-slate-500 mb-6 font-medium text-sm">You haven't purchased any plans yet. Explore our packages to get started.</p>
                        <a href="/broker-packages" className="text-blue-600 font-bold text-sm hover:underline">
                            View Plans &rarr;
                        </a>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-2 md:px-0">
                        {subscriptions.map((sub, idx) => (
                            <motion.div
                                key={sub._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-lg transition-all relative overflow-hidden group flex flex-col"
                            >
                                {/* Background Accent */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-[#05144b]/5 rounded-bl-full -mr-8 -mt-8 group-hover:bg-[#05144b]/10 transition-colors duration-500"></div>

                                <div className="relative z-10 flex-grow">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="w-14 h-14 rounded-xl bg-[#05144b]/5 text-[#05144b] flex items-center justify-center text-xl">
                                            <i className="fa-solid fa-crown"></i>
                                        </div>
                                        {sub.status === 'approved' ? (
                                            <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-md font-bold text-xs border border-emerald-100 flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active
                                            </span>
                                        ) : sub.status === 'expired' ? (
                                            <span className="px-3 py-1 bg-slate-50 text-slate-500 rounded-md font-bold text-xs border border-slate-200 flex items-center gap-2">
                                                Expired
                                            </span>
                                        ) : sub.status === 'rejected' ? (
                                            <span className="px-3 py-1 bg-rose-50 text-rose-600 rounded-md font-bold text-xs border border-rose-100 flex items-center gap-2">
                                                Rejected
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 bg-amber-50 text-amber-600 rounded-md font-bold text-xs border border-amber-100 flex items-center gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span> Pending
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                                        {sub.name}
                                    </h3>
                                    <div className="flex items-baseline gap-1 mb-6">
                                        <span className="text-3xl font-black text-[#05144b]">{sub.price}</span>
                                        <span className="text-sm font-medium text-slate-500">/ {sub.duration}</span>
                                    </div>

                                    <div className="space-y-3 pt-6 border-t border-gray-100 dark:border-slate-800">
                                        {[
                                            { label: 'Transaction ID', value: `#${sub.transactionId.slice(-8).toUpperCase()}` },
                                            { label: 'Users Allowed', value: sub.users },
                                            { label: 'Lead Access', value: sub.leadAccess },
                                            { label: 'Daily Unlock', value: sub.dailyUnlock }
                                        ].map((item, i) => (
                                            <div key={i} className="flex items-center justify-between text-sm">
                                                <span className="text-slate-500 font-medium">{item.label}</span>
                                                <span className="font-bold text-slate-900 dark:text-slate-200">
                                                    {item.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {sub.status === 'approved' && (
                                        <div className="mt-8 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800/30 rounded-lg p-5">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wide">Days Remaining</span>
                                                <span className="text-sm font-bold text-emerald-700">{calculateRemainingDays(sub.expiryDate)} Days</span>
                                            </div>
                                            <div className="w-full bg-emerald-200/50 dark:bg-emerald-800/30 h-1.5 rounded-full overflow-hidden">
                                                <div 
                                                    className="bg-emerald-500 h-full rounded-full" 
                                                    style={{ width: `${Math.min(100, (calculateRemainingDays(sub.expiryDate) || 0) * 3)}%` }}
                                                ></div>
                                            </div>
                                            <div className="mt-3 text-xs text-slate-500 font-medium flex justify-between">
                                                <span>Valid until</span>
                                                <span className="text-slate-700 font-bold">{sub.expiryDate ? new Date(sub.expiryDate).toLocaleDateString() : 'N/A'}</span>
                                            </div>
                                        </div>
                                    )}

                                    {sub.status === 'expired' && (
                                        <div className="mt-8 bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-800/30 rounded-lg p-5 text-center">
                                            <p className="text-sm font-bold text-rose-600 mb-1">Plan Expired</p>
                                            <p className="text-xs text-slate-500">Purchase a new plan to continue enjoying premium features.</p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </UserDashboardLayout>
    );
}
